import * as RT from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from './cn';

/*
  Tooltip — коралловая капсула (v1 §9.11). Слой z-tooltip — выше модалок (R12).
  Градиент brand-strong → brand-deep, чтобы белый текст 12 px проходил AA (5.3–7.1:1).
  Provider — один на дерево, в App.
*/

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayMs?: number;
  /** Управление снаружи: например, молчать, пока рядом открыто окно. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Не показывать содержимое рядом с открытой карточкой, сохраняя режим управления. */
  suppressed?: boolean;
  /** Многострочные значения графика; обычная капсула остаётся умолчанием. */
  variant?: 'label' | 'data';
}

export function Tooltip({
  content,
  children,
  side = 'top',
  delayMs = 250,
  open,
  onOpenChange,
  suppressed = false,
  variant = 'label',
}: TooltipProps) {
  return (
    <RT.Root delayDuration={delayMs} open={open} onOpenChange={onOpenChange}>
      <RT.Trigger asChild>{children}</RT.Trigger>
      {!suppressed && (
        <RT.Portal>
          <RT.Content
            side={side}
            sideOffset={8}
            collisionPadding={variant === 'data' ? 8 : undefined}
            data-slot="tooltip"
            data-variant={variant === 'data' ? 'data' : undefined}
            className={cn(
              variant === 'data'
                ? 'pointer-events-none z-tooltip max-w-80 rounded-md border border-line bg-elevated p-3 text-xs text-fg shadow-lg'
                : 'z-tooltip rounded-full bg-gradient-to-br from-brand-strong to-brand-deep px-3 py-1.5 text-xs font-medium text-on-brand shadow-brand',
              // Пружинистый pop-in из стороны триггера: keyframes в motion.css (.tooltip-anim).
              'tooltip-anim',
            )}
          >
            {content}
            <RT.Arrow
              className={variant === 'data' ? 'fill-elevated' : 'fill-brand-deep'}
              width={10}
              height={5}
            />
          </RT.Content>
        </RT.Portal>
      )}
    </RT.Root>
  );
}

export const TooltipProvider = RT.Provider;
